import {
  LEVEL_LABEL,
  CHILD_LEVEL,
  useIdentityLevel,
  type OrgLevel,
} from "@/hooks/useOrgLevel";

import { useDeskMe } from "./useDeskCan";

import { QUEUE_COLUMNS, type QueueColumn } from "./columns";

/**
 * Which Desk a reader is looking at — one surface, four configurations.
 *
 * Desk was built in a single shape, the MSP's: a queue of a client's findings,
 * with the client named in a column and grouped by which client. That is right
 * for an MSP looking down at its companies and wrong at both neighbouring
 * levels.
 *
 * * A **company** is looking at *itself*. Naming the company in every row is
 *   noise, grouping by it is a single group, and transferring to a peer is not
 *   an action they have — the only direction is up, to their MSP.
 * * A **distributor** is two levels up. Their question is which **MSP** has a
 *   backlog, not which resource is idle, so the grouping and the rollup are by
 *   MSP and the company is the detail beneath it.
 *
 * **Four configurations, not four pages.** The queue, the ticket pane, the
 * transitions and the scope rule are identical at every level; what changes is
 * which columns are worth showing and what the default grouping answers. Four
 * pages would mean four copies of the lifecycle to keep in step.
 *
 * **The level is the reader's own, never the scope bar's.** Desk is a
 * workspace, not a report: whose queue this is, who can be assigned, and what
 * "escalated to me" means all follow from the person's own organization. The
 * scope bar narrows which of *their* tickets are listed and nothing else.
 * Deriving the level from the selection instead would hand a distributor an
 * MSP's Desk — a queue that is not theirs, with actions the server refuses.
 */

export type DeskLevel = OrgLevel;

export interface LevelConfig {
  level: DeskLevel;
  /** What the organization column is called here, when it is shown at all. */
  orgLabel: string;
  /** Columns this level should not see. */
  hiddenColumns: readonly QueueColumn[];
  /** Grouping the queue opens with, or "" for none. */
  defaultGroupBy: string;
  /** What the rollup below the viewer is a list of. */
  childLabel: string;
  /** Shown when this level's queue is empty. */
  emptyTitle: string;
  emptyBody: string;
}

const CONFIGS: Record<DeskLevel, LevelConfig> = {
  company: {
    level: "company",
    orgLabel: LEVEL_LABEL.company.one,
    // Their own name in every row of their own queue, and a source column that
    // says "spike" to somebody who never chose the sources.
    hiddenColumns: ["organization", "source"],
    // One organization: grouping by it produces one group with everything in
    // it, which is a heading, not a grouping.
    defaultGroupBy: "",
    childLabel: "",
    emptyTitle: "Nothing open",
    // The common case for a healthy client, and previously unhandled — an empty
    // queue read as though something had failed to load.
    emptyBody: "No open tickets. New ones appear here as they are raised.",
  },
  msp: {
    level: "msp",
    orgLabel: LEVEL_LABEL.company.one,
    hiddenColumns: [],
    defaultGroupBy: "organization",
    childLabel: LEVEL_LABEL.company.many,
    emptyTitle: "No open tickets",
    emptyBody: "No open tickets across your companies.",
  },
  distributor: {
    level: "distributor",
    orgLabel: LEVEL_LABEL.company.one,
    hiddenColumns: [],
    // Grouped by organization, which the rollup resolves to the MSP above each
    // company: at this level the question is which MSP has a backlog.
    defaultGroupBy: "organization",
    childLabel: LEVEL_LABEL.msp.many,
    emptyTitle: "No open tickets",
    emptyBody: "No open tickets across your MSPs.",
  },
  platform: {
    level: "platform",
    orgLabel: LEVEL_LABEL.company.one,
    hiddenColumns: [],
    defaultGroupBy: "organization",
    childLabel: LEVEL_LABEL.distributor.many,
    emptyTitle: "No open tickets",
    emptyBody: "No open tickets on the platform.",
  },
};

/**
 * The one place Desk decides whose workspace this is.
 *
 * The token answers on first paint; `/desk/me/` confirms it from the user's
 * organization anchor. They agree wherever the token is current, and where
 * they could not, the server decides. Every Desk surface reads this rather
 * than asking the scope bar, so the rail, the home page and the queue cannot
 * disagree about who is looking.
 */
export function useDeskOrgLevel(): OrgLevel {
  const fromToken = useIdentityLevel();
  const { data } = useDeskMe();
  return data?.level ?? fromToken;
}

export function useDeskLevel(): LevelConfig {
  return CONFIGS[useDeskOrgLevel()];
}

/** Columns this level may show, in the shared order. */
export function columnsFor(config: LevelConfig) {
  return QUEUE_COLUMNS.filter((c) => !config.hiddenColumns.includes(c.key));
}

export const LEVEL_CONFIGS = CONFIGS;
export { CHILD_LEVEL };
